import { IPgnVariantMeta, IPgnMove, PgnVariant } from '@libs/chess/pgn';

export const EMPTY_MODEL: IModelMain = { type: 'main', count: 0, moves: [] };

/*
    ____,-------------------------------,____
    \   |           View Модель         |   /
    /___|-------------------------------|___\
*/

// ------------------------------------------
// 1. Интерфейсы

// Основная ветка
//
export interface IModelMain {
  type: 'main';
  count: number;
  moves: IModelFullMove[];
  comment?: string; // комментарий для всей партии (aka prelude)
}

// Ходы основной ветки (full move)
//
export interface IModelFullMove {
  type: 'full-move';
  white: IModelStub | IModelHalfMove;
  black: IModelStub | IModelHalfMove | undefined;
  branches?: IModelVariant[];
}

// Варианты
//
export interface IModelVariant {
  type: 'variant';
  level: number;
  variantId: string;
  entries: (IModelBlock | IModelVariant)[];
  comment?: string; // комментарий всей ветки (aka prelude)
  complex?: boolean; // нельзя инлайнить (т.к. имеет ветвистую вложенность подвариантов)
  branches?: IModelVariant[]; // дерево
  virtual?: boolean; // хвост сложной ветки, отображённый как первая подветка дерева
}

// Последовательности полуходов
//
export interface IModelBlock {
  type: 'block';
  moves: (IModelHalfMove | IModelStub)[];
}

// Заглушка ходы (троеточие)
//
export interface IModelStub {
  type: 'stub';
  move: {
    color: 'black' | 'white';
    moveFull: number;
  };
}

// Полуход (half move)
//
export interface IModelHalfMove {
  type: 'move';
  move: IPgnMove;
}

export interface IModelMoveMeta {
  score?: { type: 'cp' | 'mate'; value: number };
}

// Комментарии
//
export type IGameCommentTarget = Pick<IModelMain, 'comment' | 'type'>;
export type IVariantCommentTarget = Pick<IModelVariant, 'comment' | 'type' | 'variantId'>;
export type IMoveCommentTarget = IPgnMove & { type?: undefined }; // sic! несуществующий type для удобства проверок
export type ICommentTarget = IGameCommentTarget | IVariantCommentTarget | IMoveCommentTarget;

// ------------------------------------------
// 2. Маппинг

// Основная ветка.
//
// Правила:
// - ход разбивается, если у белых есть альтернативные ветки
// - ход разбивается, если у белых есть комментарий
// - ход может начинаться с чёрных, тогда вместо белых — stub
export function mapModel(
  entries: IPgnMove[],
  pgnMeta?: IPgnVariantMeta,
  cache?: ModelCache,
): IModelMain {
  const moves: IModelFullMove[] = [];

  let i = 0;
  while (i < entries.length) {
    const m = entries[i]!;
    const mNext = entries[i + 1];
    let fullMove: IModelFullMove;

    const cached = cache?.getFullMove(m, mNext);
    if (cached) {
      fullMove = cached;
    } else {
      const w = m.color === 'white' ? m : undefined;
      const b = m.color === 'black' ? m : mNext;

      const mustBreak = w?.alts || w?.comment !== undefined;

      // moves

      const white: IModelHalfMove | IModelStub = w
        ? { type: 'move', move: w }
        : {
            type: 'stub',
            move: { color: 'white', moveFull: m.moveFull },
          };

      const black: IModelHalfMove | IModelStub | undefined = b
        ? mustBreak
          ? {
              type: 'stub',
              move: {
                color: 'black',
                moveFull: b.moveFull,
              },
            }
          : { type: 'move', move: b }
        : undefined;

      // alternatives

      const alts = mustBreak ? w?.alts : b?.alts;
      const branches: IModelVariant[] | undefined = alts?.map((a) => mapVariant(a, 1, cache));

      // full move

      fullMove = {
        type: 'full-move',
        white,
        black,
        branches,
      };
      cache?.setFullMove(m, fullMove);
    }

    moves.push(fullMove);
    const increment = fullMove.white.type === 'move' && fullMove.black?.type === 'move' ? 2 : 1;
    i += increment;
  }

  return {
    type: 'main',
    count: entries[entries.length - 1]?.moveFull ?? 0,
    moves,
    comment: pgnMeta?.prelude,
  };
}

// Побочные ветки.
//
// Правила:
// - ветка, включающая больше 1 подветки — сложная, её нельзя инлайнить
// - ветка, включающая сложную подветку — тоже сложная
// - простые ветки инлайним
// - сложные ветки показываем как дерево
// - сложная ветка разбивается на две при первой сложной ПОДветке: начало и
//   хвост; хвост оригинальной ветки отображается в дереве как первая подветка
function mapVariant(pgnVariant: PgnVariant, level: number, cache?: ModelCache): IModelVariant {
  const cached = cache?.getVariant(level, pgnVariant);
  if (cached) {
    // FIXME: проверить в сценарии с promotion
    return cached;
  }

  const entries = pgnVariant.moves;

  const blocks: IModelVariant['entries'] = [];
  let moves: IModelBlock['moves'] = [];
  let complex = false;
  let branches: IModelVariant[] | undefined;

  const flush = () => {
    if (!moves.length) return;
    blocks.push({ type: 'block', moves });
    moves = [];
  };

  entries.forEach((m, i) => {
    if (branches) return;

    const isBlack = m.color === 'black';
    const isWhite = !isBlack;
    const isFirst = i === 0;
    const notLast = i < entries.length - 1;

    if (isFirst && isBlack) {
      moves.push({
        type: 'stub',
        move: { color: 'white', moveFull: m.moveFull },
      });
    }

    // проверка isFirst нужна для рекурсивного вызова при парсинге веток.
    // Считаем, что у первого хода варианта не может быть альтернатив (т.к.
    // парсер поднимет их вверх по ирархии вплоть до fullmove, где рекурсия не страшна)
    const alts = isFirst ? undefined : m.alts?.map((l) => mapVariant(l, level + 1, cache));
    const needBranches = alts && (alts.length > 1 || alts.find((a) => a.complex));
    if (needBranches) {
      complex = true;
    }

    if (!needBranches) {
      moves.push({ type: 'move', move: m });
    } else {
      branches = [];
      const tail = new PgnVariant({
        id: `tail-${m.id}`,
        parentMoveId: pgnVariant.parentMoveId,
        moves: entries.slice(i),
      });
      const tailModel = mapVariant(tail, level + 1, cache);
      tailModel.virtual = true;
      branches.push(tailModel);
      branches.push(...alts);
    }

    const mustStub = alts && !branches && isWhite && notLast;

    if (alts) {
      flush();
    }

    if (!branches && alts) blocks.push(...alts);

    if (mustStub) {
      moves.push({
        type: 'stub',
        move: { color: 'white', moveFull: m.moveFull },
      });
    }
  });

  flush();

  const modelVariant: IModelVariant = {
    type: 'variant',
    level,
    variantId: pgnVariant.id,
    entries: blocks,
    comment: pgnVariant.prelude,
    complex,
    branches,
  };

  cache?.setVariant(pgnVariant, modelVariant);

  return modelVariant;
}

// ------------------------------------------
// 3. Кэш

// Кэш для воссоздания модели
//
export class ModelCache {
  #fullMoves = new WeakMap<IPgnMove, IModelFullMove>();
  #variants = new WeakMap<PgnVariant, IModelVariant>();

  getFullMove(m: IPgnMove, next?: IPgnMove): IModelFullMove | null {
    const cached = this.#fullMoves.get(m);

    const noNext = !next || next.color !== 'black';
    const canCache =
      cached &&
      ((noNext && !cached.black) ||
        cached.black?.type === 'stub' ||
        cached.black?.move === next ||
        cached.black?.move === m);

    return canCache ? cached : null;
  }

  setFullMove(m: IPgnMove, f: IModelFullMove): void {
    this.#fullMoves.set(m, f);
  }

  getVariant(level: number, pgnVariant: PgnVariant): IModelVariant | null {
    const cached = this.#variants.get(pgnVariant);
    return cached && cached.level === level ? cached : null;
  }

  setVariant(pgnVariant: PgnVariant, v: IModelVariant) {
    this.#variants.set(pgnVariant, v);
  }
}
